import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ScheduleChangeLogListQueryDTO } from "../../domain/contracts/schedule-logs";
import type {
    BulkCreateClassSessionsDTO,
    BulkUpdateClassSessionInstructorsDTO,
    BulkUpdateClassSessionInstructorsPreviewDTO,
    BulkUpdateClassSessionTemplateDTO,
    BulkUpdateClassSessionTemplatePreviewDTO,
    CreateClassSessionDTO,
    FindAlternativeRoomsDTO,
    UpdateClassSessionDTO,
} from "../../domain/contracts/sessions";
import type {
    CreateClassTemplateDTO,
    UpdateClassTemplateDTO,
} from "../../domain/contracts/templates";
import { classesApi } from "./client";

// ===== Class Templates =====
export const classTemplatesKeys = {
  all: ["classes", "templates"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["classes", "templates", "list", q] as const,
  detail: (id: string) => ["classes", "templates", "detail", id] as const,
};

export function useClassTemplatesQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: classTemplatesKeys.list(q),
    queryFn: () => classesApi.templates.list(q),
  });
}

export function useClassTemplateQuery(id: string) {
  return useQuery({
    queryKey: classTemplatesKeys.detail(id),
    queryFn: () => classesApi.templates.get(id),
    enabled: !!id,
  });
}

export function useClassTemplateUsageStatsQuery(id: string) {
  return useQuery({
    queryKey: ["classes", "templates", "usage-stats", id],
    queryFn: () => classesApi.templates.getUsageStats(id),
    enabled: !!id,
  });
}

export function useClassTemplateEligibleInstructorsQuery(id: string) {
  return useQuery({
    queryKey: ["classes", "templates", "eligible-instructors", id],
    queryFn: () => classesApi.templates.getEligibleInstructors(id),
    enabled: !!id,
  });
}

export function useCreateClassTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClassTemplateDTO) =>
      classesApi.templates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ template ຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classTemplatesKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສ້າງ template ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useUpdateClassTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClassTemplateDTO) =>
      classesApi.templates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດ template ຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classTemplatesKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: classTemplatesKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດ template ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useToggleClassTemplateStatus(id: string) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (status: "active" | "inactive") =>
      classesApi.templates.toggleStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: classTemplatesKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: classTemplatesKeys.all,
      });
    },
  });

  const run = (status: "active" | "inactive") =>
    new Promise<void>((resolve, reject) => {
      base.mutate(status, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useFindAlternativeRooms() {
  return useMutation({
    mutationFn: (input: FindAlternativeRoomsDTO) =>
      classesApi.sessions.findAlternativeRooms(input),
  });
}

export function useClassSessionChangeLogsQuery(id: string) {
  return useQuery({
    queryKey: [...classSessionsKeys.detail(id), "change-logs"],
    queryFn: () => classesApi.sessions.getChangeLogs(id),
    enabled: !!id,
  });
}

// ===== Schedule Change Logs (list + detail) =====
export const scheduleChangeLogsKeys = {
  all: ["classes", "schedule-logs"] as const,
  list: (q: Partial<ScheduleChangeLogListQueryDTO>) =>
    ["classes", "schedule-logs", "list", q] as const,
  detail: (id: string) => ["classes", "schedule-logs", "detail", id] as const,
};

export function useScheduleChangeLogsQuery(
  query: Partial<ScheduleChangeLogListQueryDTO> = {},
) {
  const q: ScheduleChangeLogListQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    sessionType: query.sessionType,
    changeType: query.changeType,
    changedBy: query.changedBy,
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
    reason: query.reason,
  };
  return useQuery({
    queryKey: scheduleChangeLogsKeys.list(q),
    queryFn: () => classesApi.scheduleLogs.list(q),
  });
}

export function useScheduleChangeLogQuery(id: string) {
  return useQuery({
    queryKey: scheduleChangeLogsKeys.detail(id),
    queryFn: () => classesApi.scheduleLogs.get(id),
    enabled: !!id,
  });
}

export function useCompleteClassSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => classesApi.sessions.complete(id),
    onSuccess: () => {
      toast.success("ສຳເລັດ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classSessionsKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສຳເລັດ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useCorrectInstructorCheckInClassSession(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      wrongCheckInId: string;
      correctInstructorId: string;
      reason: string;
    }) => classesApi.sessions.correctInstructorCheckIn(sessionId, body),
    onSuccess: () => {
      toast.success("ແກ້ໄຂການກວດຊື່ຄູສອນສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classSessionsKeys.detail(sessionId),
      });
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ແກ້ໄຂການກວດຊື່ຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useDuplicateClassSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => classesApi.sessions.duplicate(id),
    onSuccess: () => {
      toast.success("ສ້າງ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສ້າງ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useDeleteClassTemplate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => classesApi.templates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: classTemplatesKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ລຶບ template ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Bulk delete mutation
export function useBulkDeleteClassTemplates() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (ids: string[]) => classesApi.templates.bulkDelete(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classTemplatesKeys.all });
    },
  });

  const run = (ids: string[]) =>
    new Promise<{ deletedCount: number }>((resolve, reject) => {
      base.mutate(ids, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Bulk toggle status mutation
export function useBulkToggleClassTemplateStatus() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (input: { ids: string[]; status: "active" | "inactive" }) =>
      classesApi.templates.bulkToggleStatus(input.ids, input.status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: classTemplatesKeys.all });
    },
  });

  const run = (input: { ids: string[]; status: "active" | "inactive" }) =>
    new Promise<{ updatedCount: number }>((resolve, reject) => {
      base.mutate(input, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== ຊົ່ວໂມງຮຽນຄລາດສ໌ =====
export const classSessionsKeys = {
  all: ["classes", "sessions"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["classes", "sessions", "list", q] as const,
  calendar: (
    start: string,
    end: string,
    filters?: {
      roomId?: string;
      instructorId?: string;
      classTypeId?: string;
      status?: string;
      templateId?: string;
    },
  ) => ["classes", "sessions", "calendar", start, end, filters] as const,
  detail: (id: string) => ["classes", "sessions", "detail", id] as const,
};

export function useClassSessionsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: classSessionsKeys.list(q),
    queryFn: () => classesApi.sessions.list(q),
  });
}

export function useClassSessionQuery(id: string) {
  return useQuery({
    queryKey: classSessionsKeys.detail(id),
    queryFn: () => classesApi.sessions.get(id),
    enabled: !!id,
  });
}

export function useSessionAttendeesQuery(sessionId: string) {
  return useQuery({
    queryKey: [...classSessionsKeys.detail(sessionId), "attendees"],
    queryFn: () => classesApi.sessions.getAttendees(sessionId),
    enabled: !!sessionId,
  });
}

export function useSessionInstructorAttendeesQuery(sessionId: string) {
  return useQuery({
    queryKey: [...classSessionsKeys.detail(sessionId), "instructor-attendees"],
    queryFn: () => classesApi.sessions.getInstructorAttendees(sessionId),
    enabled: !!sessionId,
  });
}

export function useSessionEligibleStudentsQuery(
  sessionId: string,
  search: string,
) {
  return useQuery({
    queryKey: [
      ...classSessionsKeys.detail(sessionId),
      "eligible-students",
      search,
    ],
    queryFn: () =>
      classesApi.sessions.getEligibleStudents(sessionId, {
        q: search || undefined,
        limit: 100,
      }),
    enabled: !!sessionId,
  });
}

export function useSessionAbsencesQuery(sessionId: string) {
  return useQuery({
    queryKey: [...classSessionsKeys.detail(sessionId), "absences"],
    queryFn: () => classesApi.sessions.getAbsences(sessionId),
    enabled: !!sessionId,
  });
}

export function useCalendarSessionsQuery(
  start: string,
  end: string,
  filters?: {
    roomId?: string;
    instructorId?: string;
    classTypeId?: string;
    status?: string;
    templateId?: string;
  },
) {
  return useQuery({
    queryKey: classSessionsKeys.calendar(start, end, filters),
    queryFn: () => classesApi.sessions.getCalendar(start, end, filters),
    enabled: !!start && !!end,
  });
}

export function useCheckConflictsQuery(
  start: string,
  end: string,
  roomId?: string,
) {
  return useQuery({
    queryKey: ["classes", "sessions", "check-conflicts", start, end, roomId],
    queryFn: () => classesApi.sessions.getCheckConflicts(start, end, roomId),
    enabled: !!start && !!end,
  });
}

export function useCreateClassSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClassSessionDTO) =>
      classesApi.sessions.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສ້າງ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useBulkCreateClassSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateClassSessionsDTO) =>
      classesApi.sessions.bulkCreate(input),
    onSuccess: (data) => {
      if (data.conflictingDates.length > 0 && data.createdCount === 0) {
        toast.warning(
          `ພົບຄວາມຂັດຂື້ນ ${data.conflictingDates.length} ວັນທີ — ບໍ່ສາມາດສ້າງ ຊົ່ວໂມງຮຽນ ໄດ້ ກະລຸນາກວດສອບລາຍການຂັດຂື້ນດ້ານລຸ່ມ`,
        );
      } else {
        toast.success(`ສ້າງ ${data.createdCount} ຊົ່ວໂມງຮຽນ ສຳເລັດ`);
      }
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ສ້າງ ຊົ່ວໂມງຮຽນ ບໍ່ສຳເລັດ");
    },
  });
}

export function usePreviewBulkUpdateClassSessionTemplate() {
  return useMutation({
    mutationFn: (input: BulkUpdateClassSessionTemplatePreviewDTO) =>
      classesApi.sessions.previewBulkUpdateTemplate(input),
  });
}

export function useBulkUpdateClassSessionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateClassSessionTemplateDTO) =>
      classesApi.sessions.bulkUpdateTemplate(input),
    onSuccess: (data) => {
      toast.success(`ອັບເດດແມ່ແບບ ${data.updatedCount} ຊົ່ວໂມງຮຽນ ສຳເລັດ`);
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດແມ່ແບບບໍ່ສຳເລັດ");
    },
  });
}

export function usePreviewBulkUpdateClassSessionInstructors() {
  return useMutation({
    mutationFn: (input: BulkUpdateClassSessionInstructorsPreviewDTO) =>
      classesApi.sessions.previewBulkUpdateInstructors(input),
  });
}

export function useBulkUpdateClassSessionInstructors() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateClassSessionInstructorsDTO) =>
      classesApi.sessions.bulkUpdateInstructors(input),
    onSuccess: (data) => {
      toast.success(`ອັບເດດຄູສອນ ${data.updatedCount} ຊົ່ວໂມງຮຽນ ສຳເລັດ`);
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useUpdateClassSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateClassSessionDTO) =>
      classesApi.sessions.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: classSessionsKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });
}

export function useCancelClassSession() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      classesApi.sessions.cancel(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: classSessionsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ຍົກເລີກ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ບໍ່ສຳເລັດ");
    },
  });

  const run = (id: string, reason: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(
        { id, reason },
        {
          onSuccess: () => {
            toast.success("ຍົກເລີກ ຊົ່ວໂມງຮຽນ ຄລາດສ໌ສຳເລັດ");
            resolve();
          },
          onError: (e) => reject(e),
        },
      );
    });

  return { ...base, run };
}
